import React from 'react';
import { Link } from 'react-router';
import { SetIcon } from 'components/SetIcon';
import {
  PublicSetInfo,
  useGetPublicSetRelatedQuery,
} from 'store/services/sets/publicSetsApi';
import './RelatedSets.css';

interface RelatedSetsProps {
  setCode: string;
}

const yearOf = (dateStr: string | null): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.getFullYear().toString();
  } catch {
    return '';
  }
};

const RelatedSetCard: React.FC<{ set: PublicSetInfo }> = ({ set }) => {
  const year = yearOf(set.releasedAt);
  return (
    <Link
      to={`/sets/${set.code.toLowerCase()}`}
      className="related-set-card"
      aria-label={`Browse ${set.name}`}
    >
      <div className="related-set-icon-container">
        <SetIcon setIconUrl={set.iconUrl} setName={set.name} size="md" inverted={true} />
      </div>
      <div className="related-set-content">
        <span className="related-set-name">{set.name}</span>
        <span className="related-set-meta">
          <span className="related-set-code">{set.code.toUpperCase()}</span>
          {year && (
            <>
              <span className="related-set-separator">·</span>
              <span className="related-set-year">{year}</span>
            </>
          )}
        </span>
      </div>
    </Link>
  );
};

export const RelatedSets: React.FC<RelatedSetsProps> = ({ setCode }) => {
  const { data, isLoading, isError } = useGetPublicSetRelatedQuery(setCode, { skip: !setCode });

  if (isLoading || isError || !data || data.related.length === 0) return null;

  return (
    <section className="related-sets" aria-label="Related sets">
      <h2 className="related-sets-title">Related Sets</h2>
      <div className="related-sets-grid">
        {data.related.map((set) => (
          <RelatedSetCard key={set.code} set={set} />
        ))}
      </div>
    </section>
  );
};
